import { useMemo } from 'react'

import { EditorialCellPlacement, EditorialRow, Product } from '../../../../../types/graphql'
import { TileItem, TileProduct, TitleTile } from '../sections/CollectionGridSection/Tiles/types'

export type UseEditorializedCollectionsProps = {
  collections: Array<{
    collectionScope: NonNullable<TileProduct['collectionScope']>
    products: Product[]
    editorialCellPlacements: readonly EditorialCellPlacement[]
    editorialRows: readonly EditorialRow[]
    title: string | undefined
    subtitle: string | undefined
    numItems: number
  }>
  ignoreEditorials: boolean
}

export type UseEditorializedCollectionsResults = Array<{
  collectionScope: NonNullable<TileProduct['collectionScope']>
  items: {
    mobile: TileItem[]
    desktop: TileItem[]
  }
}>

export function isEditorialCellPlacement(item: TileItem): item is EditorialCellPlacement {
  return item.__typename === 'EditorialCellPlacement'
}

export function isTitleTile(item: TileItem): item is TitleTile {
  return item.__typename === 'TitleTile'
}

export function isEditorialRow(item: TileItem): item is EditorialRow {
  return item.__typename === 'VideoSection'
}

const mapDesktopTileAlignmentToOffset = (tile: EditorialCellPlacement) => {
  const alignment = tile.alignmentDesktop

  if (alignment === '1') {
    return 0
  } else if (alignment === '2') {
    return 1
  } else if (alignment === '3') {
    return 2
  }

  return 3
}

const mergeProductsAndEditorials = ({
  editorials,
  pinnedEditorials,
  collectionScope,
  products,
  itemsPerRow,
  isMobile,
}: {
  editorials: TileItem[]
  pinnedEditorials: TileItem[]
  collectionScope: UseEditorializedCollectionsProps['collections'][number]['collectionScope']
  products: Product[]
  itemsPerRow: number
  isMobile: boolean
}) => {
  const mergedTileItems: TileItem[] = []
  let productsIndex: number = 0

  editorials.forEach((editorial, editorialsIndex) => {
    let tilesToAddPriorToEditorial = 0
    let tilesToAddAfterEditorial = 0
    const isFirstEditorial = editorialsIndex === 0

    const requestedCellWidth = !isEditorialCellPlacement(editorial) ? 4 : editorial.cellWidth ?? 1
    const cellWidth = requestedCellWidth > itemsPerRow ? itemsPerRow : requestedCellWidth

    const requestedColumnOffset = !isEditorialCellPlacement(editorial)
      ? 0
      : mapDesktopTileAlignmentToOffset(editorial)
    const columnOffset = Math.min(requestedColumnOffset, itemsPerRow - 1)

    const fallbackMobileRowRowsToSkip = isFirstEditorial ? 3 : 2
    const fallbackDesktopRowRowsToSkip = isFirstEditorial ? 2 : 1
    const fallbackRowRowsToSkip = isMobile
      ? fallbackMobileRowRowsToSkip
      : fallbackDesktopRowRowsToSkip

    const fallbackMobileCellRowsToSkip = isFirstEditorial ? 1 : 2
    const fallbackDesktopCellRowsToSkip = isFirstEditorial ? 0 : 1
    const fallbackCellRowsToSkip = isMobile
      ? fallbackMobileCellRowsToSkip
      : fallbackDesktopCellRowsToSkip

    const requestedRowsToSkip = isEditorialCellPlacement(editorial)
      ? isMobile
        ? editorial.rowsToSkipMobile
        : editorial.rowsToSkipDesktop
      : fallbackRowRowsToSkip
    const rowsToSkip = requestedRowsToSkip !== null ? requestedRowsToSkip : fallbackCellRowsToSkip

    // Skip X number of rows worth of tiles. Use provided rowsToSkipMobile if provided, otherwise skip 1 row for first item and 2 for next items.
    tilesToAddPriorToEditorial = rowsToSkip * itemsPerRow

    // Mobile editorials always fill the row, so less to do here.
    if (!isMobile) {
      // Add column offset
      tilesToAddPriorToEditorial = tilesToAddPriorToEditorial + columnOffset

      // Fill the remaining row space after the editorial cell
      if (columnOffset + cellWidth <= itemsPerRow) {
        // If we fit in the row, check how much we have filled based on the items per row
        const trailingTiles = (tilesToAddPriorToEditorial + cellWidth) % itemsPerRow
        tilesToAddAfterEditorial = trailingTiles > 0 ? itemsPerRow - trailingTiles : 0
      } else {
        // If we were too big for the row, we started our own...
        tilesToAddAfterEditorial = itemsPerRow - cellWidth
      }
    }

    if (products.length >= productsIndex + tilesToAddPriorToEditorial) {
      for (
        let loopCounter = 0;
        loopCounter < tilesToAddPriorToEditorial;
        loopCounter++, productsIndex++
      ) {
        const next = products[productsIndex]
        if (next) mergedTileItems.push({ ...next, collectionScope })
      }
      mergedTileItems.push(editorial)
      for (
        let loopCounter = 0;
        loopCounter < tilesToAddAfterEditorial;
        loopCounter++, productsIndex++
      ) {
        const next = products[productsIndex]
        if (next) mergedTileItems.push({ ...next, collectionScope })
      }
    } else {
      for (productsIndex; productsIndex < products.length; productsIndex++) {
        const next = products[productsIndex]
        if (next) mergedTileItems.push({ ...next, collectionScope })
      }
    }
  })

  // Add any lingering items, this covers the case where we have no editorials at all
  for (productsIndex; productsIndex < products.length; productsIndex++) {
    const next = products[productsIndex]
    if (next) mergedTileItems.push({ ...next, collectionScope })
  }

  // Append on pinned editorials
  pinnedEditorials.forEach(pinnedEditorial => {
    mergedTileItems.push(pinnedEditorial)
  })

  return mergedTileItems
}

export const useEditorializedCollections = ({
  collections,
  ignoreEditorials,
}: UseEditorializedCollectionsProps) => {
  const editorializedCollections = useMemo(() => {
    const editorializedCollections: UseEditorializedCollectionsResults = []

    collections.forEach(collection => {
      if (collection.products.length === 0) return

      // Sort through editorials
      const collectionIsFullyLoaded = collection.products.length === collection.numItems
      const editorials: TileItem[] = []
      const pinnedEditorials: TileItem[] = []
      for (
        let i = 0;
        i < Math.max(collection.editorialCellPlacements.length, collection.editorialRows.length);
        i++
      ) {
        if (collection.editorialCellPlacements[i]) {
          if (collection.editorialCellPlacements[i]?.pinToBottom === 'yes') {
            if (collectionIsFullyLoaded) {
              pinnedEditorials.push(collection.editorialCellPlacements[i]!)
            }
          } else {
            editorials.push(collection.editorialCellPlacements[i]!)
          }
        }
        if (collection.editorialRows[i]) {
          editorials.push(collection.editorialRows[i]!)
        }
      }

      const mobileTileItems = mergeProductsAndEditorials({
        editorials: !ignoreEditorials ? editorials : [],
        pinnedEditorials: pinnedEditorials,
        itemsPerRow: 2,
        products: collection.products,
        isMobile: true,
        collectionScope: collection.collectionScope,
      })

      const desktopTileItems = mergeProductsAndEditorials({
        editorials: !ignoreEditorials ? editorials : [],
        pinnedEditorials: pinnedEditorials,
        itemsPerRow: 4,
        products: collection.products,
        isMobile: false,
        collectionScope: collection.collectionScope,
      })

      if (collection.title && !ignoreEditorials) {
        const titleTile = {
          __typename: 'TitleTile' as const,
          id: `TitleTile:${collection.collectionScope.collectionHandle}`,
          title: collection.title,
          subtitle: collection.subtitle,
        }
        mobileTileItems.unshift(titleTile)
        desktopTileItems.unshift(titleTile)
      }

      editorializedCollections.push({
        collectionScope: collection.collectionScope,
        items: {
          mobile: mobileTileItems,
          desktop: desktopTileItems,
        },
      })
    })

    return editorializedCollections
  }, [collections, ignoreEditorials])

  return editorializedCollections
}
